import { type FirebaseApp, initializeApp } from 'firebase/app';
import { type Messaging, deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { type FCMTokensRepository, fcmTokensRepository } from '@/repositories/fcmTokens.repository';
import { makeBinded } from '@/proxies';
import { registerSW } from 'virtual:pwa-register';
import { assertDefined } from '@/lib/assert';

class FCMListenerService extends EventTarget {
  private readonly messaging: Messaging | null;
  private swReg: ServiceWorkerRegistration | null = null;
  private _interval: ReturnType<typeof setInterval> | null = null;

  private readonly _fcmTokensRepository: FCMTokensRepository = fcmTokensRepository;

  private readonly app: FirebaseApp = initializeApp(
    {
      appId: env.VITE_FB_APPID,
      projectId: env.VITE_FB_PROJECTID,
      apiKey: env.VITE_FB_APIKEY,
      authDomain: env.VITE_FB_AUTHDOMAIN,
      messagingSenderId: env.VITE_FB_SENDERID,
    },
    'firebase-notifications',
  );

  constructor() {
    super();
    try {
      this.messaging = getMessaging(this.app);
      this.listenForPermissionsChange();
    }
    catch {
      this.messaging = null;
    }
  }

  private listenForPermissionsChange() {
    try {
      navigator.permissions.query({ name: 'notifications' }).then((notificationPerm) => {
        notificationPerm.onchange = (evt) => {
          this.dispatchEvent(
            new CustomEvent('notifications-permission', {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error ts(2339)
              detail: evt.target?.state,
            }),
          );
        };
      });
    }
    catch {
      // DO NOTHING
    }
  }

  async stopListening() {
    if (!this.messaging) return;
    try {
      await deleteToken(this.messaging);
    }
    catch {
      // DO NOTHING
    }
    try {
      await this.swReg?.unregister();
    }
    catch {
      // DO NOTHING
    }
    try {
      return this._fcmTokensRepository.deleteCurrentToken();
    }
    catch {
      // DO NOTHING
    }
  }

  private _poll_for_token_change(messaging: Messaging, worker: ServiceWorkerRegistration) {
    if (this._interval) clearInterval(this._interval);
    this._interval = setInterval(() => {
      getToken(messaging, {
        serviceWorkerRegistration: worker,
      }).then((token: string) => {
        const _currentToken = fcmTokensRepository.getCurrentToken();
        if (token !== _currentToken) {
          this._fcmTokensRepository.updateCurrentToken(token);
        }
      });
    }, 30 * 1000);
  }

  /**
   *
   */
  async listen() {
    if (!this.messaging) return;
    this.swReg = await new Promise<ServiceWorkerRegistration>((res) => registerSW({
      onRegisteredSW: (_swScriptUrl, registration) => {
        assertDefined(registration, 'registration in `FCMListenerService`');
        res(registration);
      },
    }));

    onMessage(this.messaging, (evt) => {
      this.dispatchEvent(new CustomEvent('new-notification', { detail: evt }));
    });

    getToken(this.messaging, {
      serviceWorkerRegistration: this.swReg,
    }).then(this._fcmTokensRepository.updateCurrentToken);

    this._poll_for_token_change(this.messaging, this.swReg);
  }

  /**
   *
   */
  async askPermissions() {
    return Notification.requestPermission();
  }

  isSupported() {
    return isSupported();
  }
}

const fcmListenerService = makeBinded(FCMListenerService);

export { fcmListenerService };
